import { useAuthStore } from './auth';
import type { AuthResponse } from './auth';
import { API_BASE } from './apiBase';

const BASE = API_BASE;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: { error?: { code: string; message: string } },
  ) {
    super(body.error?.message ?? `API error ${status}`);
  }
}

function buildHeaders(token: string | null): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

async function doRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return false;
    const data = await res.json() as AuthResponse;
    useAuthStore.getState().setAuth(data.user, data.access_token, data.expires_in);
    return true;
  } catch {
    return false;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = useAuthStore.getState().accessToken;

  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: buildHeaders(token),
  });

  if (res.status === 401) {
    const refreshed = await doRefresh();
    if (refreshed) {
      const newToken = useAuthStore.getState().accessToken;
      const retry = await fetch(`${BASE}${path}`, {
        ...init,
        credentials: 'include',
        headers: buildHeaders(newToken),
      });
      if (!retry.ok) {
        const errBody = await retry.json().catch(() => ({})) as ApiError['body'];
        throw new ApiError(retry.status, errBody);
      }
      if (retry.status === 204) return undefined as T;
      return retry.json() as Promise<T>;
    }
    useAuthStore.getState().logout();
    throw new ApiError(401, { error: { code: 'unauthorized', message: 'Session expired.' } });
  }

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({})) as ApiError['body'];
    throw new ApiError(res.status, errBody);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PUT',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
